import { useState, memo } from 'react';

import {
  Chip,
  makeStyles,
  Accordion,
  AccordionDetails,
  AccordionSummary
} from '@material-ui/core';

import { View, List, ChevronDown } from '@carbon/icons-react';

import { colorSet } from '@components/Provider';
import { CustomizedTypography } from '@components/Customized';

import AccordionProvider from './AccordionProvider';
import PartnerCard from '../PartnerCard';

import { onClickCardParams, RFQ } from '../types';

interface RfqAccordionProps extends RFQ {
  onClickCard?: (params: onClickCardParams) => void;
}

const RfqAccordion = ({ rfq, onClickCard }: RfqAccordionProps) => {
  const classes = useStyles();

  const [expanded, setExpanded] = useState(false);

  const accordionHandler = (_: any, open: boolean) => {
    if (rfq?.receivedQuotationCount === 0) {
      return;
    }

    setExpanded(open);
  };

  if (!rfq) return null;

  const {
    uniqueViews,
    isTransaction,
    newQuotationCount,
    newIntroductionCount,
    receivedQuotationCount
  } = rfq;

  const isComplete = Boolean(isTransaction);

  const newQuotationAndIntroductionCount =
    (newQuotationCount ?? 0) + (newIntroductionCount ?? 0);

  return (
    <AccordionProvider rfqId={rfq.rfqId ?? undefined} expanded={expanded}>
      {({ data: quotations, refetch }) => (
        <Accordion
          square
          onChange={accordionHandler}
          classes={{
            root: classes.root
          }}
          elevation={0}
          expanded={expanded && (quotations?.length !== 0 ? true : false)}
        >
          <AccordionSummary
            classes={{ root: 'summary' }}
            expandIcon={<ChevronDown size={20} color={colorSet.gray900} />}
          >
            <div className="summary_preview">
              <div className="preview">
                <CustomizedTypography
                  className="count"
                  variant="body1"
                  color="textPrimary"
                  fontWeight="500"
                >
                  <View size={24} color={colorSet.gray700} />
                  &nbsp;{uniqueViews}
                </CustomizedTypography>

                <CustomizedTypography
                  className="count"
                  variant="body1"
                  color="textPrimary"
                  fontWeight="500"
                >
                  <List size={24} color={colorSet.gray700} />
                  &nbsp;{receivedQuotationCount}
                </CustomizedTypography>
              </div>
              {newQuotationAndIntroductionCount !== 0 && (
                <Chip
                  label={newQuotationAndIntroductionCount}
                  className="chip"
                />
              )}
            </div>
          </AccordionSummary>
          <AccordionDetails className="detail">
            {quotations?.map((quotation) => (
              <PartnerCard
                key={quotation.quotationId}
                quotation={quotation}
                isPurchaseOrder={isComplete}
                onClickCard={onClickCard}
                refetchAccordion={refetch}
              />
            ))}
          </AccordionDetails>
        </Accordion>
      )}
    </AccordionProvider>
  );
};

export default memo(RfqAccordion);

const useStyles = makeStyles(
  (theme) => ({
    root: {
      backgroundColor: colorSet.gray100,
      maxWidth: 375,
      minHeight: 56,
      '&::before': {
        top: 0,
        height: 0
      },
      '&.Mui-expanded': {
        margin: 0
      },
      '&:hover': {
        backgroundColor: colorSet.gray200
      },
      [theme.breakpoints.down('md')]: {
        maxWidth: '100%'
      },

      '& .summary': {
        height: 56,
        '&.Mui-expanded': {
          minHeight: 56,
          margin: 0
        },
        '&:hover': {
          '& .MuiAccordionSummary-expandIcon': {
            background: colorSet.gray300
          }
        },
        '& .summary_preview': {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          '& .preview': {
            display: 'flex',
            alignItems: 'center',
            '& > :first-child': {
              marginRight: 12
            }
          },
          '& .count': {
            display: 'flex',
            alignItems: 'center'
          },
          '& svg': {
            color: colorSet.gray600
          }
        }
      },
      '& .detail': {
        display: 'flex',
        flexDirection: 'column',
        padding: 0
      },
      '& .chip': {
        height: 20,
        backgroundColor: colorSet.errorBase,
        width: 'fit-content',
        '& span': {
          ...theme.typography.caption,
          color: colorSet.gray000,
          paddingLeft: 6.5,
          paddingRight: 6.5
        }
      }
    }
  }),
  { classNamePrefix: 'rfq-card__accordion' }
);
